import { RANKS, SUITS, type Card, type PlayedCard, type Rank, type Suit } from "./types";

const SAME_COLOR: Record<Suit, Suit> = {
  clubs: "spades",
  spades: "clubs",
  diamonds: "hearts",
  hearts: "diamonds",
};

const PLAIN_RANK: Record<Rank, number> = {
  "9": 0,
  "10": 1,
  J: 2,
  Q: 3,
  K: 4,
  A: 5,
};

export function createDeck(): Card[] {
  return SUITS.flatMap((suit) => RANKS.map((rank) => ({ id: `${suit}-${rank}`, suit, rank })));
}

export function isRightBower(card: Card, trump: Suit): boolean {
  return card.rank === "J" && card.suit === trump;
}

export function isLeftBower(card: Card, trump: Suit): boolean {
  return card.rank === "J" && card.suit === SAME_COLOR[trump];
}

export function effectiveSuit(card: Card, trump: Suit | null): Suit {
  if (trump && isLeftBower(card, trump)) return trump;
  return card.suit;
}

export function legalCards(hand: Card[], trick: PlayedCard[], trump: Suit): Card[] {
  if (trick.length === 0) return hand;
  const lead = effectiveSuit(trick[0].card, trump);
  const followers = hand.filter((card) => effectiveSuit(card, trump) === lead);
  return followers.length ? followers : hand;
}

// Facts behind an illegal-play explanation: the effective lead suit and how
// many cards of it the player still holds (Left Bower counts as trump).
export function followSuitInfo(
  hand: Card[],
  trick: PlayedCard[],
  trump: Suit,
): { leadSuit: Suit; held: number } | null {
  if (trick.length === 0) return null;
  const leadSuit = effectiveSuit(trick[0].card, trump);
  return { leadSuit, held: hand.filter((card) => effectiveSuit(card, trump) === leadSuit).length };
}

export function cardStrength(card: Card, trump: Suit, lead: Suit): number {
  if (isRightBower(card, trump)) return 200;
  if (isLeftBower(card, trump)) return 199;
  const suit = effectiveSuit(card, trump);
  if (suit === trump) return 150 + PLAIN_RANK[card.rank];
  if (suit === lead) return 100 + PLAIN_RANK[card.rank];
  return PLAIN_RANK[card.rank];
}

export function trickWinner(cards: PlayedCard[], trump: Suit): PlayedCard {
  if (!cards.length) throw new Error("Cannot score an empty trick");
  const lead = effectiveSuit(cards[0].card, trump);
  return cards.reduce((winner, entry) =>
    cardStrength(entry.card, trump, lead) > cardStrength(winner.card, trump, lead) ? entry : winner,
  );
}

export function shuffleDeck(seed: string): Card[] {
  const result = createDeck();
  const random = seededRandom(seed);
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export function seededRandom(seed: string): () => number {
  let value = hashString(seed) || 0x9e3779b9;
  return () => {
    value |= 0;
    value = (value + 0x6d2b79f5) | 0;
    let t = value;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function hashString(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function suitColor(suit: Suit): "red" | "black" {
  return suit === "hearts" || suit === "diamonds" ? "red" : "black";
}

export function partnerOf(seat: number): 0 | 1 | 2 | 3 {
  return ((seat + 2) % 4) as 0 | 1 | 2 | 3;
}

export function teamOf(seat: number): 0 | 1 {
  return (seat % 2) as 0 | 1;
}
